// Ata das reuniões semanais. Ao encerrar a reunião, guardamos o que foi tratado
// (comentários registrados nos projetos + itens de "outros assuntos"). Todos
// consultam no menu "Reuniões"; só admin cria.
import { supabase } from './supabase';

export interface MeetingItemProjeto {
  tipo: 'projeto';
  projeto: string; // "MARCA · Nome"
  productId: string | null;
  comentario: string | null;
  anexo: string | null;
  marcados: string[]; // nomes marcados (encaminhamentos)
  autor?: string | null; // quem comentou (ata completa)
  quando?: string | null; // timestamp ISO do comentário (ata completa)
}
export interface MeetingItemOutro {
  tipo: 'outro';
  assunto: string;
  responsavel: string | null;
  comentario?: string | null;
  autor?: string | null;
  quando?: string | null;
}
export type MeetingItem = MeetingItemProjeto | MeetingItemOutro;

function rel<T>(v: unknown): T | null {
  if (Array.isArray(v)) return (v[0] as T) ?? null;
  return (v as T) ?? null;
}

function prodLabel(prod: { name?: string; brands?: unknown } | null): string | null {
  if (!prod?.name) return null;
  const brand = rel<{ name?: string }>(prod.brands);
  return `${brand?.name ? `${brand.name} · ` : ''}${prod.name}`;
}

/**
 * Monta a ata a partir de TUDO que foi persistido na janela da reunião (desde
 * `sinceIso`): comentários nos projetos (stage_notes, incluindo editados) e
 * mensagens dos fios de discussão (encaminhamentos e assuntos). Assim a ata
 * reflete tudo que foi comentado/editado/tratado — mesmo que a reunião tenha
 * sido fechada e reaberta várias vezes antes de gerar a ata.
 */
export async function collectMeetingActivity(
  orgId: string,
  sinceIso: string,
): Promise<MeetingItem[]> {
  const items: MeetingItem[] = [];
  const vistos = new Set<string>(); // dedup (projeto|texto)

  // 1) Comentários nos projetos — novos ou editados dentro da janela.
  const { data: notes } = await supabase
    .from('stage_notes')
    .select('product_id, body, attachment_name, author_name, created_at, updated_at, products(name, brands(name))')
    .eq('org_id', orgId)
    .or(`created_at.gte.${sinceIso},updated_at.gte.${sinceIso}`)
    .order('created_at', { ascending: true });
  for (const r of (notes ?? []) as Record<string, unknown>[]) {
    const body = ((r.body as string) ?? '').trim();
    const anexo = (r.attachment_name as string) ?? null;
    if (!body && !anexo) continue;
    const projeto = prodLabel(rel(r.products)) ?? 'Projeto';
    vistos.add(`${projeto}|${body}`);
    items.push({
      tipo: 'projeto',
      projeto,
      productId: (r.product_id as string) ?? null,
      comentario: body || null,
      anexo,
      marcados: [],
      autor: (r.author_name as string) ?? null,
      quando: (r.created_at as string) ?? null,
    });
  }

  // 2) Mensagens dos fios (encaminhamentos e assuntos) dentro da janela.
  const { data: msgs } = await supabase
    .from('discussion_messages')
    .select('body, author_name, created_at, meeting_actions(kind, title, product_id, products(name, brands(name)))')
    .eq('org_id', orgId)
    .gte('created_at', sinceIso)
    .order('created_at', { ascending: true });
  for (const r of (msgs ?? []) as Record<string, unknown>[]) {
    const body = ((r.body as string) ?? '').trim();
    if (!body) continue;
    const act = rel<{ kind?: string; title?: string; product_id?: string; products?: unknown }>(r.meeting_actions);
    if (act?.kind === 'assunto') {
      items.push({
        tipo: 'outro',
        assunto: act.title ?? 'Assunto',
        responsavel: null,
        comentario: body,
        autor: (r.author_name as string) ?? null,
        quando: (r.created_at as string) ?? null,
      });
    } else {
      const projeto = prodLabel(rel(act?.products)) ?? act?.title ?? 'Encaminhamento';
      // Pula a 1ª mensagem que apenas repete o comentário já listado do projeto.
      if (vistos.has(`${projeto}|${body}`)) continue;
      items.push({
        tipo: 'projeto',
        projeto,
        productId: act?.product_id ?? null,
        comentario: body,
        anexo: null,
        marcados: [],
        autor: (r.author_name as string) ?? null,
        quando: (r.created_at as string) ?? null,
      });
    }
  }

  return items;
}

export interface Meeting {
  id: string;
  heldOn: string; // 'YYYY-MM-DD'
  createdByName: string | null;
  itens: MeetingItem[];
  createdAt: string;
}

export async function saveMeeting(params: {
  orgId: string;
  createdById: string | null;
  createdByName: string | null;
  itens: MeetingItem[];
}): Promise<{ error?: string; id?: string }> {
  const { data, error } = await supabase
    .from('meetings')
    .insert({
      org_id: params.orgId,
      created_by: params.createdById,
      created_by_name: params.createdByName,
      summary: { itens: params.itens },
    })
    .select('id')
    .single();
  return { error: error?.message, id: (data as { id: string } | null)?.id };
}

export async function deleteMeeting(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('meetings').delete().eq('id', id);
  return { error: error?.message };
}

// --- Rascunho (reunião em andamento) --------------------------------------
// Guarda o estado da pauta para continuar de onde parou. Um por organização.
export interface MeetingDraft {
  dispensados: string[];
  incluidos: string[];
  // Fios (encaminhamentos/assuntos) tirados da pauta desta reunião — some do topo
  // e vai para "Fora da pauta", de onde dá para voltar. Zera ao gerar a ata.
  fiosForaPauta: string[];
  // Início da reunião em andamento (ISO). Marca a janela para montar a ata
  // completa ao encerrar. Persiste entre fechar/reabrir; zera ao gerar a ata.
  startedAt: string | null;
  itens: MeetingItem[];
}

export async function loadMeetingDraft(orgId: string): Promise<MeetingDraft | null> {
  const { data } = await supabase
    .from('meeting_drafts')
    .select('state')
    .eq('org_id', orgId)
    .maybeSingle();
  if (!data) return null;
  const state = ((data as { state?: Partial<MeetingDraft> }).state ?? {}) as Partial<MeetingDraft>;
  return {
    dispensados: state.dispensados ?? [],
    incluidos: state.incluidos ?? [],
    fiosForaPauta: state.fiosForaPauta ?? [],
    startedAt: state.startedAt ?? null,
    itens: state.itens ?? [],
  };
}

export async function saveMeetingDraft(params: {
  orgId: string;
  updatedById: string | null;
  draft: MeetingDraft;
}): Promise<{ error?: string }> {
  const { error } = await supabase.from('meeting_drafts').upsert(
    {
      org_id: params.orgId,
      updated_by: params.updatedById,
      state: params.draft,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'org_id' },
  );
  return { error: error?.message };
}

export async function clearMeetingDraft(orgId: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('meeting_drafts').delete().eq('org_id', orgId);
  return { error: error?.message };
}

export async function loadMeetings(orgId: string): Promise<Meeting[]> {
  const { data } = await supabase
    .from('meetings')
    .select('id, held_on, created_by_name, summary, created_at')
    .eq('org_id', orgId)
    .order('held_on', { ascending: false })
    .order('created_at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map((r) => {
    const summary = (r.summary as { itens?: MeetingItem[] }) ?? {};
    return {
      id: r.id as string,
      heldOn: r.held_on as string,
      createdByName: (r.created_by_name as string) ?? null,
      itens: summary.itens ?? [],
      createdAt: r.created_at as string,
    };
  });
}
